import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import { useBrand } from "../context/BrandContext";
import { api, formatApiError } from "../lib/api";
import {
  DeviceMobileCamera,
  SimCard,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Lightning,
  Phone,
  Envelope,
  User,
  ChatCircleText,
  Star,
} from "@phosphor-icons/react";

const NETWORKS = [
  { id: "MTN", label: "MTN", tag: "Widest coverage" },
  { id: "Vodacom", label: "Vodacom", tag: "Most reliable" },
  { id: "Cell C", label: "Cell C", tag: "Value leader" },
  { id: "Vox", label: "Vox", tag: "Business specialist" },
  { id: "Telkom", label: "Telkom", tag: "Best data rates" },
];

const MINUTE_OPTIONS = [
  { value: "0", label: "Data only", hint: "No voice minutes" },
  { value: "100", label: "100 min", hint: "Light caller" },
  { value: "300", label: "300 min", hint: "Everyday use" },
  { value: "600", label: "600 min", hint: "Business caller" },
  { value: "1200", label: "1 200 min", hint: "Heavy use" },
  { value: "unlimited", label: "Unlimited", hint: "No cap" },
];

const DATA_OPTIONS = [
  { value: "1GB", label: "1 GB", hint: "Light browsing" },
  { value: "5GB", label: "5 GB", hint: "Email + social" },
  { value: "10GB", label: "10 GB", hint: "Streaming weekly" },
  { value: "25GB", label: "25 GB", hint: "Remote work" },
  { value: "50GB", label: "50 GB", hint: "Heavy streamer" },
  { value: "unlimited", label: "Unlimited", hint: "No cap" },
];

const DEVICES = [
  "iPhone 16 Pro",
  "iPhone 16",
  "iPhone 15",
  "Samsung Galaxy S25 Ultra",
  "Samsung Galaxy S25",
  "Samsung Galaxy A55",
  "Google Pixel 9 Pro",
  "Huawei Pura 70",
  "Xiaomi 14",
  "Let us recommend",
];

const TOTAL_STEPS = 6;

export default function BuildPackagePage() {
  const [step, setStep] = useState(1);
  const [sent, setSent] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { build_package_url } = useBrand();

  const [form, setForm] = useState({
    kind: "", // "sim" | "device"
    minutes: "",
    data: "",
    network: "",
    device: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const effectiveTotal = form.kind === "sim" ? TOTAL_STEPS - 1 : TOTAL_STEPS;

  const canNext = useMemo(() => {
    if (step === 1) return !!form.kind;
    if (step === 2) return !!form.minutes;
    if (step === 3) return !!form.data;
    if (step === 4) return !!form.network;
    if (step === 5) {
      if (form.kind === "sim") return true; // skipped
      return !!form.device;
    }
    if (step === 6) return form.name.trim() && form.email.trim() && form.phone.trim();
    return false;
  }, [step, form]);

  const goNext = () => {
    if (step === 4 && form.kind === "sim") {
      setStep(6);
    } else if (step < TOTAL_STEPS) {
      setStep(step + 1);
    }
  };
  const goBack = () => {
    if (step === 6 && form.kind === "sim") setStep(4);
    else if (step > 1) setStep(step - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitErr("");
    const minutesLabel =
      MINUTE_OPTIONS.find((m) => m.value === form.minutes)?.label || form.minutes;
    const dataLabel =
      DATA_OPTIONS.find((d) => d.value === form.data)?.label || form.data;

    const summary = [
      `Package type   : ${form.kind === "sim" ? "SIM only" : "SIM + Device"}`,
      `Preferred net  : ${form.network}`,
      `Minutes        : ${minutesLabel}`,
      `Data           : ${dataLabel}`,
      form.kind === "device" ? `Device         : ${form.device}` : null,
      form.notes ? `\nExtra notes:\n${form.notes}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Custom Package Request — ${form.network}`,
        message: `NEW CUSTOM PACKAGE REQUEST\n\n${summary}`,
      });
      setSent(true);
    } catch (e) {
      setSubmitErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <PublicLayout>
        <section className="min-h-[70vh] flex items-center justify-center py-24" data-testid="build-package-success">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-black neon-glow mb-8">
              <CheckCircle size={40} weight="fill" className="text-white" />
            </div>
            <div className="overline text-[#a8b2cc] mb-5">Request received</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
              We'll build it<br />
              <span className="neon-text">and get back to you.</span>
            </h1>
            <p className="mt-8 text-lg text-[#a8b2cc] leading-relaxed">
              Thanks <strong className="text-[#f5ecd9]">{form.name}</strong>. A consultant
              will contact you on <strong>{form.phone}</strong> within one business day
              with tailored options on <strong>{form.network}</strong>.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/" className="btn-ghost" data-testid="success-home">Back to home</Link>
              <Link to="/offers" className="btn-neon" data-testid="success-offers">
                Browse Current Offers <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Seo
        title="Build Your Package"
        description="Tell us how you call, scroll and connect — we'll match you with the right MTN, Vodacom, Cell C, Vox or Telkom package in under two minutes."
        path="/build-package"
      />
      <section className="relative pt-10 pb-20 overflow-hidden text-center bg-[#141e32] text-white" data-testid="build-package-page">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[110vw] h-[520px] rainbow-swoosh pointer-events-none" />
        <InfinityMotif variant="wireframe" className="absolute top-4 left-1/2 -translate-x-1/2 w-[1200px] max-w-[130vw] h-auto" opacity={0.16} />
        <div className="relative max-w-[1000px] mx-auto px-6">
          {build_package_url ? (
            <div className="mx-auto mb-8 w-24 h-24 rounded-[24px] overflow-hidden border border-[#2c3854] bg-[#070b14]">
              <img src={build_package_url} alt="Build your package" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="flex justify-center mb-7">
              <Star size={24} weight="fill" className="text-[#dda632]" />
            </div>
          )}
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />
          <div className="eyebrow mb-5" style={{ color: "#dda632" }}>Build Your Own Package</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98]">
            Build the contract<br />
            <span className="rainbow-text">you actually want.</span>
          </h1>
          <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto">
            Tell us how much you call, how much you scroll, and which network you trust. We'll do
            the negotiating — it takes under two minutes.
          </p>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      <section className="border-b border-[#2c3854] bg-[#141e32] sticky top-[74px] z-30">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-5">
          <div className="flex items-center gap-3">
            <span className="font-mono-accent text-[0.7rem] tracking-[0.22em] uppercase text-[#a8b2cc] min-w-fit">
              Step {step > 4 && form.kind === "sim" ? step - 1 : step} / {effectiveTotal}
            </span>
            <div className="flex-1 h-[3px] bg-[#141e32] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-500"
                style={{
                  width: `${((step > 4 && form.kind === "sim" ? step - 1 : step) / effectiveTotal) * 100}%`,
                  background: "linear-gradient(135deg, #55a2d2, #af3fa6, #dda632)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          {step === 1 && (
            <StepShell
              title="Start with the basics."
              subtitle="Do you want a contract with a device, or just a SIM on a rate plan?"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ChoiceCard
                  active={form.kind === "sim"}
                  onClick={() => update({ kind: "sim", device: "" })}
                  icon={<SimCard size={40} weight="duotone" />}
                  title="SIM only"
                  caption="No device — just minutes & data on your preferred network."
                  testid="kind-sim"
                />
                <ChoiceCard
                  active={form.kind === "device"}
                  onClick={() => update({ kind: "device" })}
                  icon={<DeviceMobileCamera size={40} weight="duotone" />}
                  title="SIM + Device"
                  caption="Get a new phone bundled with minutes & data over 24–36 months."
                  testid="kind-device"
                />
              </div>
            </StepShell>
          )}

          {step === 2 && (
            <StepShell
              title="How many minutes?"
              subtitle="Think about your busiest month — pick the size that fits."
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {MINUTE_OPTIONS.map((m) => (
                  <OptionCard
                    key={m.value}
                    active={form.minutes === m.value}
                    onClick={() => update({ minutes: m.value })}
                    primary={m.label}
                    secondary={m.hint}
                    testid={`minutes-${m.value}`}
                  />
                ))}
              </div>
            </StepShell>
          )}

          {step === 3 && (
            <StepShell
              title="How much data?"
              subtitle="Streaming, remote work, hotspotting — tell us your average."
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {DATA_OPTIONS.map((d) => (
                  <OptionCard
                    key={d.value}
                    active={form.data === d.value}
                    onClick={() => update({ data: d.value })}
                    primary={d.label}
                    secondary={d.hint}
                    testid={`data-${d.value}`}
                  />
                ))}
              </div>
            </StepShell>
          )}

          {step === 4 && (
            <StepShell
              title="Preferred network?"
              subtitle="We partner with every major SA carrier — pick the one you trust."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {NETWORKS.map((n) => (
                  <OptionCard
                    key={n.id}
                    active={form.network === n.id}
                    onClick={() => update({ network: n.id })}
                    primary={n.label}
                    secondary={n.tag}
                    testid={`network-${n.id.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))}
              </div>
              <p className="mt-5 text-sm text-[#a8b2cc] font-mono-accent uppercase tracking-[0.18em]">
                Not sure? We'll recommend based on coverage & price.
              </p>
            </StepShell>
          )}

          {step === 5 && form.kind === "device" && (
            <StepShell
              title="Which device?"
              subtitle="Pick a favourite, or tell us your budget and we'll suggest."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DEVICES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => update({ device: d })}
                    data-testid={`device-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex items-center justify-between px-5 py-4 border text-left transition-all ${
                      form.device === d
                        ? "border-[#f5ecd9] bg-[#141e32] text-white"
                        : "border-[#2c3854] bg-[#141e32] hover:border-[#f5ecd9]"
                    }`}
                  >
                    <span className="font-display text-lg tracking-tight">{d}</span>
                    {form.device === d && <CheckCircle size={20} weight="fill" className="text-[#dda632]" />}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {step === 6 && (
            <StepShell
              title="Where should we call you?"
              subtitle="A consultant will reach out within one business day with your tailored package."
            >
              <Summary form={form} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <LabeledInput
                  icon={<User size={16} weight="bold" />}
                  label="Full name"
                  value={form.name}
                  onChange={(v) => update({ name: v })}
                  testid="pkg-input-name"
                />
                <LabeledInput
                  icon={<Phone size={16} weight="bold" />}
                  label="Phone"
                  value={form.phone}
                  onChange={(v) => update({ phone: v })}
                  testid="pkg-input-phone"
                />
                <div className="md:col-span-2">
                  <LabeledInput
                    icon={<Envelope size={16} weight="bold" />}
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => update({ email: v })}
                    testid="pkg-input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block">
                    <span className="flex items-center gap-2 font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
                      <ChatCircleText size={14} weight="bold" /> Notes (optional)
                    </span>
                    <textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => update({ notes: e.target.value })}
                      placeholder="Anything else — number of SIMs, billing preferences, device budget..."
                      className="w-full border border-[#2c3854] focus:border-[#f5ecd9] outline-none p-3 resize-none"
                      data-testid="pkg-input-notes"
                    />
                  </label>
                </div>
              </div>
              {submitErr && (
                <div className="mt-5 border border-[#af3fa6] text-[#af3fa6] p-3 text-sm" data-testid="pkg-submit-error">
                  {submitErr}
                </div>
              )}
            </StepShell>
          )}

          <div className="mt-12 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 1}
              className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pkg-back-button"
            >
              <ArrowLeft size={14} weight="bold" /> Back
            </button>

            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canNext}
                className="btn-neon disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pkg-next-button"
              >
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canNext || submitting}
                className="btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pkg-submit-button"
              >
                {submitting ? "Submitting…" : "Send Request"}
                <Lightning size={16} weight="fill" />
              </button>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const StepShell = ({ title, subtitle, children }) => (
  <div className="fade-up">
    <div className="overline text-[#a8b2cc] mb-4">Let's build</div>
    <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tighter leading-[0.95] max-w-3xl">
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-lg text-[#a8b2cc] max-w-2xl">{subtitle}</p>}
    <div className="mt-10">{children}</div>
  </div>
);

const ChoiceCard = ({ active, onClick, icon, title, caption, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-8 lg:p-10 rounded-[32px] border-2 transition-all ${
      active
        ? "border-[#af3fa6] bg-[#141e32] shadow-[0_20px_40px_-20px_rgba(175,63,166,0.35)]"
        : "border-[#2c3854] bg-[#141e32] hover:border-[#f5ecd9]"
    }`}
  >
    <div className="flex items-start justify-between">
      <div className={active ? "text-[#af3fa6]" : "text-[#f5ecd9]"}>{icon}</div>
      {active && <CheckCircle size={24} weight="fill" className="text-[#af3fa6]" />}
    </div>
    <div className="font-display text-3xl mt-8">{title}</div>
    <div className="mt-3 text-[#a8b2cc]">{caption}</div>
  </button>
);

const OptionCard = ({ active, onClick, primary, secondary, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-5 rounded-2xl border-2 transition-all ${
      active
        ? "border-[#af3fa6] bg-[#141e32] shadow-[0_12px_28px_-16px_rgba(175,63,166,0.35)]"
        : "border-[#2c3854] bg-[#141e32] hover:border-[#f5ecd9]"
    }`}
  >
    <div className="font-display text-2xl flex items-center justify-between text-[#f5ecd9]">
      {primary}
      {active && <CheckCircle size={18} weight="fill" className="text-[#af3fa6]" />}
    </div>
    <div className="mt-2 text-sm text-[#a8b2cc]">{secondary}</div>
  </button>
);

const LabeledInput = ({ icon, label, testid, type = "text", value, onChange }) => (
  <label className="block">
    <span className="flex items-center gap-2 font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
      {icon} {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      data-testid={testid}
      className="w-full border border-[#2c3854] focus:border-[#f5ecd9] outline-none p-3"
    />
  </label>
);

const Summary = ({ form }) => {
  const minutesLabel =
    MINUTE_OPTIONS.find((m) => m.value === form.minutes)?.label || "—";
  const dataLabel = DATA_OPTIONS.find((d) => d.value === form.data)?.label || "—";
  return (
    <div className="border border-[#f5ecd9] bg-[#141e32] text-white p-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pkg-summary">
      <SummaryCell label="Package" value={form.kind === "sim" ? "SIM only" : "SIM + Device"} />
      <SummaryCell label="Network" value={form.network || "—"} />
      <SummaryCell label="Minutes" value={minutesLabel} />
      <SummaryCell label="Data" value={dataLabel} />
      {form.kind === "device" && (
        <SummaryCell label="Device" value={form.device || "—"} wide />
      )}
    </div>
  );
};

const SummaryCell = ({ label, value, wide }) => (
  <div className={wide ? "col-span-2 md:col-span-4" : ""}>
    <div className="overline text-[#a8b2cc] mb-1">{label}</div>
    <div className="font-display text-lg tracking-tight">{value}</div>
  </div>
);
